/**
 * Suppliers / Vendors Master — with 3-step onboarding workflow
 *   DRAFT → PENDING_PM_APPROVAL → PENDING_CFO_APPROVAL → ACTIVE
 */
import { useState } from 'react'
import {
  Button, Form, Input, Select, Switch, Tag, Space, App, Divider,
  Upload, InputNumber, Tooltip, Alert, Modal, Popconfirm,
} from 'antd'
import type { UploadFile } from 'antd'
import {
  PlusOutlined, EditOutlined, SendOutlined,
  UploadOutlined, DownloadOutlined, ExclamationCircleOutlined,
  UnlockOutlined, StopOutlined, CheckCircleOutlined,
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { suppliersApi } from '@/features/gate/api'
import { supplierCategoriesApi } from '@/features/categories/api'
import apiClient from '@/shared/api/client'
import type { Supplier, SupplierStatus } from '@/shared/types/master'
import DataTable from '@/shared/components/DataTable'
import FormDrawer from '@/shared/components/FormDrawer'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'

const PAYMENT_TYPES = [
  { value: 'ADVANCE', label: 'Advance Payment (pay before delivery)' },
  { value: 'CASH',    label: 'Cash on Delivery / Immediate' },
  { value: 'CREDIT',  label: 'Credit (NET-N days)' },
]

const STATUS_COLORS: Record<SupplierStatus, string> = {
  DRAFT:                'default',
  PENDING_PM_APPROVAL:  'orange',
  PENDING_CFO_APPROVAL: 'gold',
  ACTIVE:               'green',
  REJECTED:             'red',
  INACTIVE:             'default',
  CHANGES_REQUESTED:    'volcano',
}

const STATUS_LABELS: Record<SupplierStatus, string> = {
  DRAFT:                'Draft',
  PENDING_PM_APPROVAL:  'Pending PM',
  PENDING_CFO_APPROVAL: 'Pending CFO',
  ACTIVE:               'Active',
  REJECTED:             'Rejected',
  INACTIVE:             'Inactive',
  CHANGES_REQUESTED:    'Changes Requested',
}

const DOC_TYPES = [
  { key: 'pan',    label: 'PAN Card',         required: true },
  { key: 'gstin',  label: 'GSTIN Certificate', required: true },
  { key: 'cheque', label: 'Cancelled Cheque', required: true },
  { key: 'msme',   label: 'MSME Certificate', required: false },
  { key: 'iso',    label: 'ISO Certificate',  required: false },
]

export default function SuppliersPage() {
  const { message, modal } = App.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<Supplier | null>(null)
  const [uploadTarget, setUploadTarget] = useState<{ vendor: Supplier; docType: string } | null>(null)

  const canCreate = usePermission('supplier.create')
  const canUpdate = usePermission('supplier.update')
  const canApprove = usePermission('supplier.approve')

  // ── Request edit (unlocks an ACTIVE / PENDING / REJECTED vendor) ──
  // Optional self-approval: if user has supplier.approve, the same flow
  // creates the request AND approves it in one shot — quickest catch-up
  // path for admins. Otherwise the request goes to the approver inbox.
  const [editRequestVendor, setEditRequestVendor] = useState<Supplier | null>(null)
  const [editReason, setEditReason] = useState('')
  const [editSelfApprove, setEditSelfApprove] = useState(canApprove)

  // Sprint 0065 — Deactivate / Reactivate flow
  const [deactivateVendor, setDeactivateVendor] = useState<Supplier | null>(null)
  const [deactReason, setDeactReason] = useState('')
  const [deactivateMode, setDeactivateMode] = useState<'OFF' | 'ON'>('OFF')

  const deactivateMutation = useMutation({
    mutationFn: async (input: { vendor: Supplier; reason: string; mode: 'OFF' | 'ON' }) => {
      const action = input.mode === 'OFF' ? 'deactivate' : 'reactivate'
      return await apiClient.post(`/suppliers/${input.vendor.id}/${action}`, {
        reason: input.reason,
      }).then(r => r.data)
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['suppliers'] })
      setDeactivateVendor(null)
      setDeactReason('')
      message.success(
        vars.mode === 'OFF'
          ? `Vendor ${vars.vendor.supplier_name} deactivated`
          : `Vendor ${vars.vendor.supplier_name} reactivated`,
      )
    },
    onError: (e: any) =>
      message.error(e.response?.data?.detail || 'Action failed'),
  })

  const requestEditMutation = useMutation({
    // Edit-lock router is mounted at /edit-lock/edit-requests, NOT
    // /edit-requests. The previous URL returned 404 ("not found error")
    // for every Request Edit click on this page.
    mutationFn: async (input: { vendor: Supplier; reason: string; selfApprove: boolean }) => {
      const created = await apiClient.post('/edit-lock/edit-requests', {
        entity_name: 'suppliers',
        entity_id: input.vendor.id,
        reason: input.reason,
      }).then(r => r.data)
      if (input.selfApprove && canApprove) {
        await apiClient.post(`/edit-lock/edit-requests/${created.id}/action`, {
          status: 'APPROVED',
          remarks: 'Self-approved by admin (catch-up workflow)',
        })
      }
      return { created, selfApproved: input.selfApprove && canApprove }
    },
    onSuccess: (res, vars) => {
      qc.invalidateQueries({ queryKey: ['suppliers'] })
      setEditRequestVendor(null)
      setEditReason('')
      if (res.selfApproved) {
        message.success(`Vendor unlocked — open Edit on ${vars.vendor.supplier_name}`)
      } else {
        message.success('Edit request raised — waiting for approver')
      }
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Request failed'),
  })

  const { data, isLoading } = useQuery({
    queryKey: ['suppliers', page, pageSize, search, statusFilter],
    queryFn: () => suppliersApi.list({
      page, size: pageSize,
      search: search || undefined,
      status: statusFilter,
    }),
  })

  const { data: categories = [] } = useQuery({
    queryKey: ['supplier-categories'],
    queryFn: () => supplierCategoriesApi.list(true),
  })

  // Inline "Add New Category" state
  const [catModalOpen, setCatModalOpen] = useState(false)
  const [catForm] = Form.useForm()
  const addCategoryMutation = useMutation({
    mutationFn: (v: { code: string; name: string }) => supplierCategoriesApi.create(v),
    onSuccess: (cat) => {
      qc.invalidateQueries({ queryKey: ['supplier-categories'] })
      message.success(`Category "${cat.name}" added`)
      form.setFieldValue('supplier_type', cat.code)  // auto-select new one
      setCatModalOpen(false)
      catForm.resetFields()
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Failed'),
  })

  // Sprint 0063 — Numbering Series Master integration. Auto-generated
  // code preview with edit-toggle for legacy / Tally-compat overrides.
  const [codeOverride, setCodeOverride] = useState(false)
  const [nextCodePreview, setNextCodePreview] = useState<string>('')

  const fetchNextSupplierCode = async () => {
    try {
      const { data } = await apiClient.get('/numbering-series/next-code', {
        params: { document_type: 'SUPPLIER' },
      })
      setNextCodePreview(data?.next_code ?? '')
      form.setFieldValue('supplier_code', data?.next_code ?? '')
    } catch {
      setNextCodePreview('')
    }
  }

  const openCreate = () => {
    setEditing(null)
    setCodeOverride(false)
    form.resetFields()
    form.setFieldsValue({ country: 'India', supplier_type: 'OTHER', payment_type: 'CREDIT', credit_days: 30 })
    setDrawerOpen(true)
    void fetchNextSupplierCode()
  }

  // Watch payment_type to conditionally show credit_days
  const paymentType = Form.useWatch('payment_type', form)

  const openEdit = (record: Supplier) => {
    setEditing(record)
    form.setFieldsValue(record)
    setDrawerOpen(true)
  }

  const saveMutation = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      editing ? suppliersApi.update(editing.id, values) : suppliersApi.create(values),
    onSuccess: (s: Supplier) => {
      qc.invalidateQueries({ queryKey: ['suppliers'] })
      message.success(editing ? 'Vendor updated' : 'Vendor created as DRAFT — upload documents then submit for approval')
      setEditing(s)  // keep drawer open in edit mode so user can upload docs
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.detail ?? 'Save failed'),
  })

  const submitMutation = useMutation({
    mutationFn: (id: string) => suppliersApi.submit(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['suppliers'] })
      message.success('Submitted for Purchase Manager approval')
      setDrawerOpen(false)
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.detail ?? 'Submit failed — check all required fields + documents are present'),
  })

  const uploadMutation = useMutation({
    mutationFn: ({ id, docType, file }: { id: string; docType: string; file: File }) =>
      suppliersApi.uploadDocument(id, docType, file),
    onSuccess: (s: Supplier) => {
      qc.invalidateQueries({ queryKey: ['suppliers'] })
      setEditing(s)
      message.success('Document uploaded')
      setUploadTarget(null)
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.detail ?? 'Upload failed'),
  })

  const onSubmitForm = () => form.validateFields().then((v) => saveMutation.mutate(v))

  const columns = [
    { title: 'Code', dataIndex: 'supplier_code', key: 'code', width: 90 },
    { title: 'Name', dataIndex: 'supplier_name', key: 'name' },
    {
      title: 'Type', dataIndex: 'supplier_type', key: 'type',
      render: (v: string) => <Tag>{v.replace(/_/g, ' ')}</Tag>,
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status',
      render: (v: SupplierStatus, r: Supplier) => (
        <Space direction="vertical" size={0}>
          <Tag color={STATUS_COLORS[v] ?? 'default'}>{STATUS_LABELS[v] ?? v}</Tag>
          {r.needs_review && (
            <Tooltip title="Grandfathered vendor — needs full re-review">
              <Tag color="volcano" icon={<ExclamationCircleOutlined />} style={{ fontSize: 10 }}>
                Needs Review
              </Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: 'GSTIN', dataIndex: 'gstin', key: 'gstin',
      render: (v?: string, r?: Supplier) => v
        ? <Space size={4}>
            <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span>
            {r?.gstin_verified && <Tag color="green" style={{ fontSize: 10 }}>✓</Tag>}
          </Space>
        : '—',
    },
    {
      title: 'Expected Annual', dataIndex: 'expected_annual_value', key: 'eav',
      render: (v?: string) => v
        ? `₹${Number(v).toLocaleString('en-IN')}`
        : '—',
    },
    { title: 'City', dataIndex: 'city', key: 'city', render: (v?: string) => v ?? '—' },
    {
      title: 'Actions', key: 'actions',
      render: (_: unknown, record: Supplier) => {
        const isEditableStatus = ['DRAFT', 'CHANGES_REQUESTED', 'INACTIVE'].includes(record.status)
        const isLockedStatus   = ['ACTIVE', 'PENDING_PM_APPROVAL', 'PENDING_CFO_APPROVAL', 'REJECTED'].includes(record.status)
        return (
          <Space size={4} wrap>
            {canUpdate && (
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => openEdit(record)}
              >
                {isEditableStatus ? 'Edit' : 'View'}
              </Button>
            )}
            {canUpdate && isLockedStatus && (
              <Tooltip title={canApprove
                ? "Unlock for editing (admins can self-approve in one step)"
                : "Raise edit request — approver will review"}>
                <Button size="small" icon={<UnlockOutlined />}
                  onClick={() => {
                    setEditRequestVendor(record)
                    setEditReason('')
                    setEditSelfApprove(canApprove)
                  }}>
                  Request Edit
                </Button>
              </Tooltip>
            )}
            {canUpdate && isEditableStatus && (
              <Popconfirm
                title="Submit for Purchase Manager approval?"
                description="Make sure all fields + documents are complete."
                onConfirm={() => submitMutation.mutate(record.id)}
                okText="Submit"
              >
                <Button size="small" type="primary" ghost icon={<SendOutlined />}>
                  Submit
                </Button>
              </Popconfirm>
            )}
            {/* Sprint 0065 — Deactivate / Reactivate */}
            {canUpdate && record.is_active && (
              <Button size="small" danger icon={<StopOutlined />}
                onClick={() => {
                  setDeactivateVendor(record)
                  setDeactReason('')
                  setDeactivateMode('OFF')
                }}>
                Deactivate
              </Button>
            )}
            {canUpdate && !record.is_active && (
              <Button size="small" type="primary" ghost
                icon={<CheckCircleOutlined />}
                onClick={() => {
                  setDeactivateVendor(record)
                  setDeactReason('')
                  setDeactivateMode('ON')
                }}>
                Reactivate
              </Button>
            )}
          </Space>
        )
      },
    },
  ]

  const renderDocUploadRow = (doc: { key: string; label: string; required: boolean }) => {
    if (!editing) return null
    const urlField = `docs_${doc.key}_url` as keyof Supplier
    const uploaded = editing[urlField] as string | undefined
    return (
      <div key={doc.key} style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 0', borderBottom: '1px solid #f0f0f0',
      }}>
        <Space>
          <span>{doc.label}</span>
          {doc.required && <Tag color="red" style={{ fontSize: 10 }}>Required</Tag>}
          {uploaded && <Tag color="green" style={{ fontSize: 10 }}>Uploaded</Tag>}
        </Space>
        <Space>
          {uploaded && (
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => window.open(suppliersApi.getDocumentUrl(editing.id, doc.key), '_blank')}
            >
              View
            </Button>
          )}
          <Upload
            maxCount={1}
            showUploadList={false}
            beforeUpload={(file) => {
              uploadMutation.mutate({ id: editing.id, docType: doc.key, file })
              return false  // prevent default upload
            }}
          >
            <Button size="small" icon={<UploadOutlined />}>
              {uploaded ? 'Replace' : 'Upload'}
            </Button>
          </Upload>
        </Space>
      </div>
    )
  }

  return (
    <div>
      <PageHeader
        title="Suppliers & Vendors"
        subtitle="3-step onboarding: Purchase Officer creates → Manager approves → CFO final approval"
        extra={canCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            New Vendor
          </Button>
        )}
      />

      <Space style={{ marginBottom: 16 }}>
        <Input.Search placeholder="Search name, code…" allowClear
          onSearch={(v) => { setSearch(v); setPage(1) }} style={{ width: 280 }} />
        <Select
          placeholder="Filter by status"
          allowClear
          style={{ width: 180 }}
          value={statusFilter}
          onChange={(v) => { setStatusFilter(v); setPage(1) }}
          options={Object.keys(STATUS_LABELS).map(k => ({
            value: k, label: STATUS_LABELS[k as SupplierStatus],
          }))}
        />
      </Space>

      <DataTable rowKey="id" columns={columns} dataSource={data?.items} loading={isLoading}
        total={data?.total} page={page} pageSize={pageSize} onPageChange={(p, ps) => { setPage(p); setPageSize(ps) }} />

      <FormDrawer
        title={editing ? `Vendor: ${editing.supplier_name} (${STATUS_LABELS[editing.status]})` : 'New Vendor'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={onSubmitForm}
        loading={saveMutation.isPending}
        form={form}
        width={720}
      >
        {editing?.status === 'REJECTED' && (
          <Alert
            type="error"
            message="Vendor was rejected"
            description={editing.rejection_reason || editing.approval_remarks}
            showIcon
            style={{ marginBottom: 12 }}
          />
        )}
        {editing?.status === 'CHANGES_REQUESTED' && (
          <Alert
            type="warning"
            message="Changes requested by Purchase Manager"
            description={editing.approval_remarks || 'Please review and update.'}
            showIcon
            style={{ marginBottom: 12 }}
          />
        )}
        {editing?.status === 'ACTIVE' && editing.needs_review && (
          <Alert
            type="info"
            message="Legacy vendor — review pending"
            description="This vendor existed before the approval workflow was introduced. Please verify all details and re-submit for formal approval."
            showIcon
            style={{ marginBottom: 12 }}
          />
        )}

        <Divider orientation="left" plain>Basic Info</Divider>
        {/* Sprint 0063 — Vendor code preview with edit toggle (auto by
            default, Tally-compat override on demand). Hidden in edit
            mode — codes are immutable once assigned. */}
        {!editing && (
          <Form.Item name="supplier_code"
            label={
              <Space size={4}>
                <span>Vendor Code</span>
                {!codeOverride && (
                  <span style={{ fontSize: 10, color: '#1677ff' }}>(auto)</span>
                )}
                <a style={{ fontSize: 11 }}
                   onClick={() => {
                     setCodeOverride(v => !v)
                     if (!codeOverride) {
                       form.setFieldValue('supplier_code', '')
                     } else {
                       void fetchNextSupplierCode()
                     }
                   }}>
                  {codeOverride ? '↺ Use auto' : '✎ Edit code'}
                </a>
              </Space>
            }
            rules={[{ required: codeOverride, message: 'Code required when overriding' }]}>
            <Input placeholder={nextCodePreview || 'auto-generated'}
              disabled={!codeOverride}
              readOnly={!codeOverride} />
          </Form.Item>
        )}
        <Form.Item name="supplier_name" label="Vendor Name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="supplier_type" label="Vendor Category" rules={[{ required: true }]}>
          <Select
            showSearch
            optionFilterProp="label"
            options={categories.map(c => ({ value: c.code, label: c.name }))}
            dropdownRender={(menu) => (
              <>
                {menu}
                <Divider style={{ margin: '4px 0' }} />
                <Button
                  type="link"
                  icon={<PlusOutlined />}
                  onClick={() => setCatModalOpen(true)}
                  style={{ width: '100%', textAlign: 'left' }}
                >
                  Add new category
                </Button>
              </>
            )}
          />
        </Form.Item>
        <Form.Item name="contact_person" label="Contact Person"><Input /></Form.Item>
        <Form.Item name="phone" label="Phone"><Input /></Form.Item>
        <Form.Item name="email" label="Email"><Input type="email" /></Form.Item>

        <Divider orientation="left" plain>Address</Divider>
        <Form.Item name="address" label="Address"><Input.TextArea rows={2} /></Form.Item>
        <Form.Item name="city" label="City"><Input /></Form.Item>
        <Form.Item name="state" label="State"><Input /></Form.Item>
        <Form.Item name="country" label="Country" initialValue="India"><Input /></Form.Item>
        <Form.Item name="pincode" label="Pincode"><Input maxLength={6} /></Form.Item>

        <Divider orientation="left" plain>Tax / Legal</Divider>
        <Form.Item name="gstin" label="GSTIN" help="15 characters (format validated)">
          <Input maxLength={15} style={{ fontFamily: 'monospace' }} />
        </Form.Item>
        <Form.Item name="pan" label="PAN" help="10 characters">
          <Input maxLength={10} style={{ fontFamily: 'monospace' }} />
        </Form.Item>
        <Form.Item name="msme_reg_no" label="MSME Registration No. (if applicable)">
          <Input />
        </Form.Item>

        <Divider orientation="left" plain>Bank Details</Divider>
        <Form.Item name="bank_name" label="Bank Name"><Input /></Form.Item>
        <Form.Item name="bank_branch" label="Branch"><Input /></Form.Item>
        <Form.Item name="account_number" label="Account Number"><Input maxLength={30} /></Form.Item>
        <Form.Item name="ifsc_code" label="IFSC Code" help="Format: AAAA0NNNNNN">
          <Input maxLength={11} style={{ fontFamily: 'monospace' }} />
        </Form.Item>

        <Divider orientation="left" plain>Commercial Terms</Divider>
        <Form.Item name="expected_annual_value" label="Expected Annual Value (₹)" rules={[{ required: true, message: 'Required for CFO routing' }]}>
          <InputNumber style={{ width: '100%' }} min={0} formatter={(v) => `₹ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} />
        </Form.Item>
        <Form.Item name="payment_type" label="Payment Type" rules={[{ required: true }]}>
          <Select options={PAYMENT_TYPES} />
        </Form.Item>
        {paymentType === 'CREDIT' && (
          <Form.Item
            name="credit_days"
            label="Credit Days (NET-N)"
            rules={[{ required: true, message: 'Required for credit terms' }]}
            help="Number of days before payment is due (e.g. 30 for NET-30)"
          >
            <InputNumber style={{ width: '100%' }} min={1} max={365} />
          </Form.Item>
        )}
        <Form.Item name="credit_limit" label="Credit Limit (₹)">
          <InputNumber style={{ width: '100%' }} min={0} />
        </Form.Item>
        <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item>

        {editing && (
          <>
            <Divider orientation="left" plain>Documents</Divider>
            {DOC_TYPES.map(renderDocUploadRow)}
          </>
        )}
      </FormDrawer>

      {/* ── Request Edit modal ─────────────────────────────────────── */}
      <Modal
        title={
          <Space>
            <UnlockOutlined style={{ color: '#1677ff' }} />
            Request Edit — {editRequestVendor?.supplier_name ?? ''}
          </Space>
        }
        open={!!editRequestVendor}
        onCancel={() => { setEditRequestVendor(null); setEditReason('') }}
        confirmLoading={requestEditMutation.isPending}
        okText={editSelfApprove && canApprove
          ? 'Unlock now'
          : 'Submit request'}
        okButtonProps={{ disabled: editReason.trim().length < 5 }}
        onOk={() => {
          if (!editRequestVendor) return
          requestEditMutation.mutate({
            vendor: editRequestVendor,
            reason: editReason.trim(),
            selfApprove: editSelfApprove,
          })
        }}
      >
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message={`Status: ${editRequestVendor?.status}`}
          description={
            <span style={{ fontSize: 12 }}>
              Live vendor records are edit-locked to protect master data.
              {' '}Provide a reason — the change will be auditable.
              {canApprove && (
                <span> Admins can <b>self-approve</b> for catch-up scenarios.</span>
              )}
            </span>
          }
        />

        <Form layout="vertical">
          <Form.Item label="Reason" required
            help="Why does this vendor need editing? (5–500 chars)">
            <Input.TextArea
              rows={3}
              value={editReason}
              onChange={e => setEditReason(e.target.value)}
              placeholder="e.g. Tally import — fixing GSTIN / bank account / contact details"
              maxLength={500}
              showCount
            />
          </Form.Item>

          {canApprove && (
            <Form.Item>
              <Space>
                <Switch checked={editSelfApprove}
                  onChange={setEditSelfApprove} />
                <span style={{ fontSize: 13 }}>
                  <b>Self-approve immediately</b> (admin shortcut)
                </span>
              </Space>
              <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>
                When ON, vendor unlocks instantly and you can edit.
                When OFF, an approver must review first.
              </div>
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* Sprint 0065 — Deactivate / Reactivate confirm modal */}
      <Modal
        title={
          <Space>
            {deactivateMode === 'OFF' && <StopOutlined style={{ color: '#ff4d4f' }} />}
            {deactivateMode === 'ON' && <CheckCircleOutlined style={{ color: '#52c41a' }} />}
            {deactivateMode === 'OFF'
              ? `Deactivate — ${deactivateVendor?.supplier_name ?? ''}`
              : `Reactivate — ${deactivateVendor?.supplier_name ?? ''}`}
          </Space>
        }
        open={!!deactivateVendor}
        onCancel={() => { setDeactivateVendor(null); setDeactReason('') }}
        confirmLoading={deactivateMutation.isPending}
        okText={deactivateMode === 'OFF' ? 'Deactivate' : 'Reactivate'}
        okButtonProps={{
          danger: deactivateMode === 'OFF',
          disabled: deactReason.trim().length < 5,
        }}
        onOk={() => {
          if (!deactivateVendor) return
          deactivateMutation.mutate({
            vendor: deactivateVendor,
            reason: deactReason.trim(),
            mode: deactivateMode,
          })
        }}
      >
        <Alert
          type={deactivateMode === 'OFF' ? 'warning' : 'info'}
          showIcon style={{ marginBottom: 12 }}
          message={deactivateMode === 'OFF'
            ? 'This vendor will be hidden from new-transaction pickers.'
            : 'This vendor will be restored to the active list.'}
          description={
            <span style={{ fontSize: 12 }}>
              {deactivateMode === 'OFF'
                ? 'Historical GRNs / POs / invoices stay linked. The vendor can be reactivated later. Reason is recorded in the audit log.'
                : 'The previous deactivation audit (who/when/why) is cleared from the row but preserved in the audit log.'}
            </span>
          }
        />

        <Form layout="vertical">
          <Form.Item label="Reason" required
            help={`Why ${deactivateMode === 'OFF' ? 'deactivating' : 'reactivating'}? (5–500 chars)`}>
            <Input.TextArea
              rows={3}
              value={deactReason}
              onChange={e => setDeactReason(e.target.value)}
              placeholder={deactivateMode === 'OFF'
                ? 'e.g. vendor closed business; replaced by ABC Cotton; rates uncompetitive'
                : 'e.g. vendor reopened; resuming with revised terms'}
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Form>

        {deactivateMode === 'ON' && deactivateVendor?.deactivation_reason && (
          <Alert
            type="info" showIcon style={{ marginTop: 8 }}
            message="Previous deactivation reason"
            description={
              <span style={{ fontSize: 12 }}>
                {deactivateVendor.deactivation_reason}
                {deactivateVendor.deactivated_at && (
                  <span style={{ color: '#888' }}>
                    {' '}— {new Date(deactivateVendor.deactivated_at).toLocaleDateString()}
                  </span>
                )}
              </span>
            }
          />
        )}
      </Modal>

      {/* Inline "Add New Category" modal */}
      <Modal
        title="Add New Vendor Category"
        open={catModalOpen}
        onCancel={() => setCatModalOpen(false)}
        onOk={() => catForm.validateFields().then((v) => addCategoryMutation.mutate(v))}
        confirmLoading={addCategoryMutation.isPending}
      >
        <Form form={catForm} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item name="code" label="Code"
            rules={[{ required: true, pattern: /^[A-Z0-9_]+$/, message: 'Uppercase + underscores only' }]}>
            <Input placeholder="e.g. FOREIGN_AGENT" style={{ fontFamily: 'monospace', textTransform: 'uppercase' }} />
          </Form.Item>
          <Form.Item name="name" label="Display Name" rules={[{ required: true }]}>
            <Input placeholder="e.g. Foreign Agent" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
